import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Briefcase, FileText, ChevronRight, Users, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OpportunitiesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const canDelete = user?.role === 'Administrador' || user?.role === 'Redator Sênior';

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/opportunities`, { withCredentials: true });
      setOpportunities(res.data);
    } catch (error) {
      toast.error('Erro ao carregar oportunidades');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (e, oppId, oppName) => {
    e.stopPropagation();
    if (!window.confirm(`Excluir o perfil "${oppName}"?`)) return;
    try {
      await axios.delete(`${API_URL}/opportunities/${oppId}`);
      toast.success('Oportunidade excluída');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const filtered = opportunities.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return o.name?.toLowerCase().includes(s) || o.client_name?.toLowerCase().includes(s) || o.tipo_documento?.toLowerCase().includes(s);
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Perfil do Cliente</h1>
          <p className="text-sm text-[#6E6E73] mt-1">Diagnóstico e perfil de oportunidades de negócio</p>
        </div>
        <Button onClick={() => navigate('/documents/new')} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-opportunity-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Documento
        </Button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
        <Input placeholder="Buscar por nome, cliente ou tipo..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="opportunity-search" />
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(opp => (
            <div
              key={opp.opportunity_id}
              onClick={() => navigate(`/opportunities/${opp.opportunity_id}`)}
              className="bg-white border border-[#E5E5EA] rounded-xl p-5 hover:shadow-md hover:border-[#AF2628]/30 transition-all cursor-pointer group"
              data-testid={`opportunity-card-${opp.opportunity_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="bg-[#FFF1F0] w-10 h-10 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#AF2628]" />
                </div>
                <div className="flex items-center gap-1">
                  {canDelete && (
                    <button
                      onClick={(e) => handleDelete(e, opp.opportunity_id, opp.name)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-[#AEAEB2] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      data-testid={`delete-opportunity-${opp.opportunity_id}`}
                      title="Excluir oportunidade"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <ChevronRight className="w-4 h-4 text-[#AEAEB2] group-hover:text-[#AF2628] transition-colors" />
                </div>
              </div>
              <h3 className="font-semibold text-sm text-[#111111] group-hover:text-[#AF2628] transition-colors truncate">{opp.name}</h3>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Users className="w-3.5 h-3.5 text-[#AEAEB2]" />
                <span className="text-xs text-[#6E6E73] truncate">{opp.client_name}</span>
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs text-[#AEAEB2]">
                <span className="bg-gray-100 px-2 py-0.5 rounded-full">{opp.tipo_documento}</span>
                <span>{opp.product_names?.length || 0} produto(s)</span>
                <span>{opp.answers_count || 0} resposta(s)</span>
              </div>
              {opp.documents_count > 0 && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-emerald-600">
                  <FileText className="w-3.5 h-3.5" />
                  <span>{opp.documents_count} documento(s) gerado(s)</span>
                </div>
              )}
              <p className="text-[11px] text-[#AEAEB2] mt-3">{new Date(opp.updated_at).toLocaleDateString('pt-BR')}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5EA] rounded-xl p-10 text-center">
          <Briefcase className="w-12 h-12 mx-auto mb-3 text-[#E5E5EA]" />
          <p className="text-sm text-[#6E6E73]">Nenhum perfil de cliente cadastrado</p>
          <p className="text-xs text-[#AEAEB2] mt-1">Crie um perfil no wizard de novo documento</p>
        </div>
      )}
    </div>
  );
};

export default OpportunitiesPage;
